import { ToggleButton as MuiToggleButton, useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  KpiDropdownActions,
  KpiType,
  StorePerformanceInterval,
  createStorePerformanceRequest,
} from '../../../../../constants';
import {
  QueueLengthCheckoutExperience,
  QueueLengthForecastPerformanceItem,
  QueueLengthPerformanceItem,
  useStorePerformance,
  useStores,
  useTransaction,
  useUser,
} from '../../../../../hooks';
import { useCelebrateTarget } from '../../../../../hooks/useCelebrateTarget';
import { Container, Icon, Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, DropdownMenu } from '../../../../../stories/molecules';
import { ToggleButton } from '../../../../../stories/molecules/toggleButton/ToggleButton';
import { muiTheme } from '../../../../../theme';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import {
  formatAmountWithSeparator,
  getKpiTargetProps,
  getKpiTargetValue,
  isFailureResponse,
} from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { isMultiDayInterval } from '../../../util';
import { GraphView } from './graphView/GraphView';
import { GridView } from './gridView/GridView';
import { MobileGrid } from './mobileGrid/MobileGrid';
import { getOverAllQueueInformation, isQueueLengthForecastPerformanceItem } from './util';

export type QueueLengthProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  isToday: boolean;
  isKpiLoading: boolean;
  isKpiError: boolean;
  hasPermissionToEditKpi: boolean;
  handleDropdownDetails: (kpiType: KpiType, action: KpiDropdownActions) => void;
  onTargetAchieved?: (targetValue: string, updatedDate: Date) => void;
};

export const QueueLengthWidget: FC<QueueLengthProps> = observer(
  ({
    interval,
    currentDate,
    isToday,
    hasPermissionToEditKpi,
    isKpiLoading,
    isKpiError,
    handleDropdownDetails,
    onTargetAchieved,
  }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
    const { queueLength: queueLengthTranslations, kpiTargets: kpiTargetsTranslations } = homeTranslations;
    const { fetchQueueLengthData } = useTransaction();
    const { fetchForecastForKpis } = useStores();
    const {
      get: { kpiTargetsProps, userPermissions },
    } = useUser();

    const canViewTillGrid = userPermissions.queueLengthTillViewWidget.canRead;

    const target = getKpiTargetValue(kpiTargetsProps, 'dailyQueueLength');
    const isTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailyQueueLength')?.isLocked ?? false;

    const [checkoutExperience, setCheckoutExperience] = useState<QueueLengthCheckoutExperience | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [view, setView] = useState('view1');

    const handleChangeView = (event: React.MouseEvent<HTMLElement>, newView: string) => {
      if (newView !== null) {
        setView(newView);
      }
    };

    const {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,

      filteredPerformanceItems,

      forecastPerformanceItems,
      triggerGetStorePerformance,
    } = useStorePerformance<QueueLengthPerformanceItem>();

    const fetchQueueLength = useCallback(async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchQueueLengthData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setCheckoutExperience(response.data.checkoutExperience);
      setLastUpdated(response.data.lastUpdated);

      return response.data.queueLength;
    }, [interval, currentDate]);

    const fetchForecastData = useCallback(async () => {
      const response = await fetchForecastForKpis<QueueLengthPerformanceItem>('queueLength');

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    }, [currentDate]);

    const fetchWidgetData = () => {
      triggerGetStorePerformance({
        interval: interval,
        currentDate: currentDate,
        isToday: isToday,
        getDataAction: fetchQueueLength,
        getForecastDataAction: fetchForecastData,
        getHasValue: (item) => {
          // Forecast data type does not match the dnormal data type. Need to manually cast here
          const performanceOrForecastItem = item as
            | QueueLengthPerformanceItem
            | QueueLengthForecastPerformanceItem;

          if (isQueueLengthForecastPerformanceItem(performanceOrForecastItem)) {
            return (
              performanceOrForecastItem.defaultTransactionsCount !== 0 ||
              performanceOrForecastItem.scoTransactionsCount !== 0
            );
          }

          const { tillData } = item;

          return !!tillData.find((tillDataItem) => tillDataItem.queueCount > 0 || tillDataItem.transactions > 0);
        },
      });
    };

    useEffect(() => {
      fetchWidgetData();
    }, [currentDate]);

    const isEditKPIDisabled = performanceItemsError || !hasPermissionToEditKpi;

    const { overallQueueLengthPercentage } = getOverAllQueueInformation({
      checkoutExperience,
      filteredPerformanceItems,
    });

    const isNoData = overallQueueLengthPercentage === 0;

    const isPreviousDay = isMultiDayInterval(interval) || !isToday;

    useCelebrateTarget({
      target,
      currentValue: overallQueueLengthPercentage,
      isToday,
      localStorageKey: 'queueLengthTargetAchieved',
      lastUpdated: lastUpdated ?? currentDate,
      onTargetAchieved: (targetSet, updatedDate) => {
        const formattedTarget = formatAmountWithSeparator(targetSet, '%');
        if (onTargetAchieved) {
          onTargetAchieved(formattedTarget, updatedDate);
        }
      },
    });

    return (
      <WidgetCardContent
        headerIcon={Icons.PROFILE}
        headerText={t(queueLengthTranslations.title.key, queueLengthTranslations.title.defaultValue)}
        subHeaderText={t(queueLengthTranslations.subTitle.key, queueLengthTranslations.subTitle.defaultValue)}
        isLoading={performanceItemsLoading}
        isError={performanceItemsError}
        errorText={t(queueLengthTranslations.error.key, queueLengthTranslations.error.defaultValue)}
        errorRetryHandler={fetchWidgetData}
        isNoData={isNoData}
        lastUpdatedDate={lastUpdated}
        toolTipText={t(queueLengthTranslations.toolTip.key, queueLengthTranslations.toolTip.defaultValue)}
        noDataContent={
          <ColumnFlexContainer data-testid="no-data-target" justifyContent="center" alignItems="center">
            <Animation
              size="small"
              titleType="h4"
              title={`${t(
                kpiTargetTranslations.kpiProperties.target.title.key,
                kpiTargetTranslations.kpiProperties.target.title.defaultValue,
              )}: ${
                target
                  ? formatAmountWithSeparator(target, '%')
                  : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue)
              }`}
              subtitle={t(queueLengthTranslations.noData.key, queueLengthTranslations.noData.defaultValue)}
              animation={Animations.COOL}
            />
          </ColumnFlexContainer>
        }
        headerSideContent={
          <RowFlexContainer alignItems="center">
            {performanceItems.length > 0 && canViewTillGrid && (
              <ToggleButton
                value={view}
                onChange={handleChangeView}
                leftButton={
                  <MuiToggleButton value="view1" aria-label="left button">
                    <Icon data-testid="left-icon" icon={Icons.TRENDUP} size="small" />
                  </MuiToggleButton>
                }
                rightButton={
                  <MuiToggleButton value="view2" aria-label="left button">
                    <Icon data-testid="right-icon" icon={Icons.ALLWIDGETS} size="small" />
                  </MuiToggleButton>
                }
              />
            )}

            <DropdownMenu
              data-testid="kpiDropdown"
              dropdownLabel={''}
              buttonContentProps={{
                iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
              }}
              menuItems={[
                {
                  key: target ? 'editkpi' : 'addkpi',
                  disabled: isEditKPIDisabled || isTargetLocked || isPreviousDay,
                  hasTooltip: isEditKPIDisabled || isTargetLocked || isPreviousDay,
                  tooltipText: (
                    <Container padding={[1]}>
                      <Typography color="white">
                        {!hasPermissionToEditKpi
                          ? t(
                              kpiTargetsTranslations.noPermissionTooltip.key,
                              kpiTargetsTranslations.noPermissionTooltip.defaultValue,
                            )
                          : isTargetLocked
                          ? t(
                              kpiTargetsTranslations.lockedTargetTooltip.key,
                              kpiTargetsTranslations.lockedTargetTooltip.defaultValue,
                            )
                          : isPreviousDay
                          ? t(
                              kpiTargetsTranslations.previousDayTooltip.key,
                              kpiTargetsTranslations.previousDayTooltip.defaultValue,
                            )
                          : t(
                              kpiTargetsTranslations.disabledTooltip.key,
                              kpiTargetsTranslations.disabledTooltip.defaultValue,
                            )}
                      </Typography>
                    </Container>
                  ),
                  item: (
                    <Typography margin={[0, 2, 0]}>
                      {target
                        ? t(kpiTargetsTranslations.editKpi.key, kpiTargetsTranslations.editKpi.defaultValue)
                        : t(kpiTargetsTranslations.addKpi.key, kpiTargetsTranslations.addKpi.defaultValue)}
                    </Typography>
                  ),
                },
              ]}
              minWidth={'0px'}
              isCompact
              initialSelected={[]}
              onSelect={(selectedItem) => {
                handleDropdownDetails('dailyQueueLength', selectedItem[0] as KpiDropdownActions);
              }}
              isOnlyIcon
              allowTooltip
            />
          </RowFlexContainer>
        }
        content={
          <ColumnFlexContainer flex={1} minHeight={[0]} data-testid="queue-length-wrapper">
            <Container wrap="nowrap" data-testid="chart" style={{ flex: 1, minHeight: 0 }}>
              {view === 'view1' && (
                <GraphView
                  target={target}
                  interval={interval}
                  checkoutExperience={checkoutExperience}
                  performanceItems={performanceItems}
                  filteredPerformanceItems={filteredPerformanceItems}
                  forecastPerformanceItems={forecastPerformanceItems}
                  isKpiError={isKpiError}
                  isKpiLoading={isKpiLoading}
                />
              )}

              {view === 'view2' && !isMobile && (
                <GridView
                  target={target}
                  performanceItems={performanceItems}
                  filteredPerformanceItems={filteredPerformanceItems}
                  interval={interval}
                />
              )}

              {view === 'view2' && isMobile && (
                <MobileGrid
                  interval={interval}
                  performanceItems={performanceItems}
                  filteredPerformanceItems={filteredPerformanceItems}
                />
              )}
            </Container>
          </ColumnFlexContainer>
        }
      />
    );
  },
);
